import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useState, ComponentProps } from "react";
import ReactDOM from "react-dom";
import {
  ArrowLeft as IconArrowLeft,
  Menu as IconMenu,
  X as IconX
} from "react-feather";
import { Button } from "../../blocks/Button";
import { useNavigation } from "../../hooks/useNavigation";
import { usePortal } from "../../hooks/usePortal";
import { ReactComponent as Logo } from "./assets/logo.svg";
import { ItemsList, itemVariants } from "./blocks/ItemsList";
import { LocaleLinks } from "./blocks/LocaleLinks";
import { MobileSearch } from "./blocks/MobileSearch";
import { Panel } from "./blocks/Panel";
import "./SiteNavigation.scss";
import { Item } from "./Types";

interface Props {
  items: Item[];
  navigate: ReturnType<typeof useNavigation>;
  travelPassSearchUrl: string;
  formAction: string;
  paramName: string;
  lang: ComponentProps<typeof MobileSearch>["lang"] & {
    buyTicketButtonAction: string;
  };
}

export const SiteNavigation: React.FC<Props> = props => {
  const portal = usePortal();
  const [visitedItems, setVisitedItems] = useState<Item[]>([]);
  const [pages, setPages] = useState<Item[][]>([]);

  const variant = pages.length === 0 ? "closed" : "open";
  const lastVisitedItem =
    visitedItems.length === 0
      ? undefined
      : visitedItems[visitedItems.length - 1];

  const openNav = useCallback(() => {
    setPages([props.items]);
    setVisitedItems([]);
  }, [props.items]);

  const closeNav = useCallback(() => {
    setPages([]);
    setVisitedItems([]);
  }, []);

  const navigateToItem = useCallback(
    (item: Item) => {
      closeNav();
      props.navigate(item.href);
    },
    [props, closeNav]
  );

  const pushPage = useCallback((item: Item) => {
    setPages(prevValue => [...prevValue, item.children!]);
    setVisitedItems(prevValue => [...prevValue, item]);
  }, []);

  const popPage = useCallback(() => {
    setPages(nextValue => nextValue.slice(0, nextValue.length - 1));
    setVisitedItems(nextValue => nextValue.slice(0, nextValue.length - 1));
  }, []);

  return (
    <>
      <div className="Icon" onClick={openNav}>
        <IconMenu size={22} style={{ opacity: 0.64 }} />
      </div>
      {ReactDOM.createPortal(
        <Panel variant={variant}>
          <div className="SiteNavigation__Header">
            <Logo height={16} />
            <div className="SiteNavigation__IconX" onClick={closeNav}>
              <IconX />
            </div>
          </div>
          <div className="SiteNavigation__Pager">
            <AnimatePresence>
              {pages.map((items, idx) => (
                <motion.div
                  key={idx}
                  className="SiteNavigation__Frame"
                  initial={{ x: idx === 0 ? "0%" : "100%" }}
                  animate={{ x: `${(pages.length - idx - 1) * -100}%` }}
                  exit={{ x: "100%" }}
                  transition={{ damping: 1000, stiffness: 1000 }}
                >
                  <div className="SiteNavigation__FrameHeader">
                    {idx === 0 && (
                      <MobileSearch
                        lang={props.lang}
                        formAction={props.formAction}
                        paramName={props.paramName}
                      />
                    )}
                    {idx > 0 && lastVisitedItem && (
                      <div
                        className="SiteNavigation__NavigateBack"
                        onClick={popPage}
                      >
                        <IconArrowLeft />
                        <span>{lastVisitedItem.title}</span>
                      </div>
                    )}
                  </div>
                  <ItemsList
                    variant={variant}
                    items={items}
                    onItemClicked={item => {
                      if (
                        item.children === undefined ||
                        item.children.length === 0
                      ) {
                        navigateToItem(item);
                      } else {
                        pushPage(item);
                      }
                    }}
                  >
                    {idx === 0 && (
                      <>
                        <motion.div
                          className="SiteNavigation__Link"
                          style={{ marginTop: 20 }}
                          variants={itemVariants}
                        >
                          <LocaleLinks />
                        </motion.div>
                      </>
                    )}
                  </ItemsList>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          <div className="SiteNavigation__Footer">
            <Button
              accent="primary"
              size="lg"
              block
              raised
              onClick={() => props.navigate(props.travelPassSearchUrl)}
            >
              {props.lang.buyTicketButtonAction}
            </Button>
          </div>
        </Panel>,
        portal
      )}
    </>
  );
};
